// components/features/faq/FaqForm.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";
import { BaseFaq, Faq } from "../../../types/features/Faq";
import { kafintech_products } from "../../../constantes/kafintech-product";
import { useProduct } from "../../../hooks/core/useProduct";

interface FaqFormProps {
  initialData?: Faq;
  isEdit?: boolean;
  onSubmit: (data: BaseFaq) => Promise<void>;
  isLoading?: boolean;
  isSuccess: boolean;
}

export default function FaqForm({ initialData, isEdit = false, onSubmit, isLoading = false, isSuccess }: FaqFormProps) {
  const navigate = useNavigate();
  const {selectedProduct} = useProduct()
  const [formData, setFormData] = useState<BaseFaq>({
    question: "",
    question_en: "",
    answer: "",
    answer_en: "",
    order: 0,
    is_visible: true,
    category_id: null,
    product_name: selectedProduct || "all",
    product_name_en: selectedProduct || "all",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        question: initialData.question,
        question_en: initialData.question_en,
        answer: initialData.answer,
        answer_en: initialData.answer_en,
        order: initialData.order,
        is_visible: initialData.is_visible,
        category_id: initialData.category_id || null,
        product_name: initialData.product_name || "all",
        product_name_en: initialData.product_name_en || "all",
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSubmit(formData);
    if (isSuccess) {
      setFormData({
        question: "",
        question_en: "",
        answer: "",
        answer_en: "",
        order: 0,
        is_visible: true,
        category_id: null,
        product_name: "all",
        product_name_en: "all",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* 📦 Produit concerné */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Produit concerné <span className="text-red-500">*</span>
          </label>
          <select
            name="product_name"
            value={formData.product_name || "all"}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="all">Tous les produits</option>
            {kafintech_products.map((product) => (
              <option key={product.value} value={product.value}>
                {product.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Sélectionnez "Tous les produits" si cette FAQ concerne tous les produits
          </p>
        </div>

        {/* Catégorie */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
          <select
            name="category_id"
            value={formData.category_id || ""}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
          >
            <option value="">Sélectionner une catégorie</option>
            <option value="general">Général</option>
            <option value="account">Compte</option>
            <option value="payment">Paiement</option>
            <option value="transfert">Transfert</option>
            <option value="technical">Technique</option>
          </select>
        </div>

        {/* Français */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Français</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question (FR) *</label>
              <input
                type="text"
                name="question"
                value={formData.question}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Comment créer un compte ?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Réponse (FR) *</label>
              <textarea
                name="answer"
                value={formData.answer}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Pour créer un compte, cliquez sur..."
              />
            </div>
          </div>
        </div>

        {/* English */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">English</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question (EN) *</label>
              <input
                type="text"
                name="question_en"
                value={formData.question_en}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="How to create an account ?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Answer (EN) *</label>
              <textarea
                name="answer_en"
                value={formData.answer_en}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="To create an account, click on..."
              />
            </div>
          </div>
        </div>

        {/* Ordre et visibilité */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
              placeholder="1, 2, 3..."
            />
            <p className="text-xs text-gray-500 mt-1">Plus le nombre est petit, plus la FAQ remonte</p>
          </div>
          <div>
            <label className="flex items-center gap-2 mt-7">
              <input
                type="checkbox"
                name="is_visible"
                checked={formData.is_visible}
                onChange={handleCheckbox}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Afficher cette FAQ sur le site</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
        </button>
      </div>
    </form>
  );
}